import React from 'react';
import { MapPinIcon, HomeIcon, UsersIcon, ClockIcon, PackageIcon, CalendarIcon, ShieldCheckIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import type { StringKey } from '../../data/i18n';
import { hrs, usd } from '../../utils/format';
import { bedroomLabel } from './StepSize';
const homeKeys: Record<string, StringKey> = {
  apartment: 'htApartment',
  house: 'htHouse',
  townhome: 'htTownhome',
  office: 'htOffice'
};
const arrivalKeys: Record<string, StringKey> = {
  '8am': 'arr8',
  noon: 'arrNoon',
  '3pm': 'arr3',
  other: 'arrOther'
};
const officeLabel = {
  en: {
    small: 'Small office',
    medium: 'Medium office',
    large: 'Large office'
  },
  es: {
    small: 'Oficina pequeña',
    medium: 'Oficina mediana',
    large: 'Oficina grande'
  }
};
const truckHelp = {
  en: 'truck: help me choose',
  es: 'camión: ayúdenme a elegir'
};
export function QuoteSummary({
  onEdit
}: {
  onEdit?: (step: number) => void;
}) {
  const {
    t,
    lang,
    data,
    quote
  } = useFunnel();
  const itemCount = Object.values(data.inventory).reduce((s, q) => s + q, 0) + data.customItems.reduce((s, c) => s + c.qty, 0);
  const rows: {
    icon: React.ReactNode;
    label: string;
    value: string;
    step: number;
  }[] = [{
    icon: <HomeIcon className="h-4 w-4" data-id="element-576" data-mp="orqodxdfqilt" />,
    label: t('stepSize'),
    value: [data.homeType ? t(homeKeys[data.homeType]) : '', data.homeType === 'office' ? data.officeSize ? officeLabel[lang][data.officeSize] : '' : data.bedrooms ? bedroomLabel(data.bedrooms, t) : ''].filter(Boolean).join(' · ') || '—',
    step: 0
  }, {
    icon: <MapPinIcon className="h-4 w-4" data-id="element-577" data-mp="633lnliqe5be" />,
    label: t('stepAddresses'),
    value: data.originZip && data.destZip ? [data.originZip, ...data.stops.map(s => s.zip || '?'), data.destZip].join(' → ') : '—',
    step: 1
  }, {
    icon: <PackageIcon className="h-4 w-4" data-id="element-578" data-mp="3sbcwmcrwe6hr" />,
    label: t('stepInventory'),
    value: itemCount ? t('invItems', {
      n: itemCount
    }) : '—',
    step: 3
  }, {
    icon: <CalendarIcon className="h-4 w-4" data-id="element-579" data-mp="3dm1sok61xtxd" />,
    label: t('stepDate'),
    value: data.dateUnsure ? t('dateUnsure') : [data.moveDate, data.arrival ? t(arrivalKeys[data.arrival]) : ''].filter(Boolean).join(' · ') || '—',
    step: 5
  }, {
    icon: <UsersIcon className="h-4 w-4" data-id="element-580" data-mp="34ng2c2uc9am1" />,
    label: t('crewLabel'),
    value: data.bedrooms || data.officeSize ? [t('moversN', {
      n: quote.crew.used
    }), data.truckPreference ? data.truckPreference === 'help' ? truckHelp[lang] : `${data.truckPreference} ft` : ''].filter(Boolean).join(' · ') : '—',
    step: 4
  }, {
    icon: <ClockIcon className="h-4 w-4" data-id="element-581" data-mp="58kske1tykr9" />,
    label: t('serviceLabel'),
    value: data.bedrooms || data.officeSize ? t('hoursRange', {
      a: hrs(quote.serviceLow),
      b: hrs(quote.serviceHigh)
    }) : '—',
    step: 7
  }];
  return <div className="rounded-2xl bg-white p-5 ring-1 ring-ink-200" data-id="element-582" data-mp="k40n0k6boio6">
      <h2 className="font-display text-lg font-extrabold text-ink" data-id="element-583" data-mp="k2pn4vfzzv2c">{t('summary')}</h2>
      <dl className="mt-3 divide-y divide-ink-100" data-id="element-584" data-mp="owaa7sg1z4mr">
        {rows.map(r => <div key={r.label} className="flex items-start justify-between gap-3 py-2.5" data-id="element-585" data-mp="y8hk15yacp2q">
            <dt className="flex items-center gap-2 text-sm text-ink-500" data-id="element-586" data-mp="u8qv77sbkj0u">
              <span className="text-ink-400" aria-hidden="true" data-id="element-587" data-mp="17z2r7hs0z1x9">
                {r.icon}
              </span>
              {r.label}
            </dt>
            <dd className="text-right text-sm font-semibold text-ink" data-id="element-588" data-mp="130sswyb07l7f">
              {r.value}
              {onEdit && r.value !== '—' && <button type="button" onClick={() => onEdit(r.step)} className="ml-2 text-xs font-medium text-magenta-dark underline underline-offset-2" data-id="element-589" data-mp="b9cxqfvdsq5l">
                  {t('editDetails')}
                </button>}
            </dd>
          </div>)}
      </dl>
      <div className="mt-3 rounded-xl bg-ink p-4 text-white" data-id="element-590" data-mp="kc4d2qrdo62i">
        <div className="flex items-baseline justify-between gap-2" data-id="element-591" data-mp="20mq4ebqyuq9v">
          <span className="text-sm text-ink-300" data-id="element-592" data-mp="1vpg9igkt5men">{t('rateLabel', {
            n: quote.crew.used
          })}</span>
          <span className="whitespace-nowrap text-sm font-bold" data-id="element-593" data-mp="58clubt0lruu">{quote.hourlyRate !== null ? `${usd(quote.hourlyRate, lang)}${t('perHour')}` : t('rateByTeam')}</span>
        </div>
        <div className="mt-2 flex items-baseline justify-between" data-id="element-594" data-mp="f60khctqrjia">
          <span className="text-sm text-ink-300" data-id="element-595" data-mp="f4ped6ctpzo0">{t('lineDeposit')}</span>
          <span className="font-display text-xl font-black text-lime" data-id="element-596" data-mp="j8nijvh0a0nx">{usd(quote.deposit, lang)}</span>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-ink-300" data-id="element-597" data-mp="o65pxhfdk7tt">{t('depositCredit')}</p>
      </div>
      <p className="mt-3 flex items-center gap-2 text-xs text-ink-500" data-id="element-598" data-mp="t47a65ojed6v">
        <ShieldCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-599" data-mp="xxjkrbwdsjgm" />
        {t('saved')}
      </p>
    </div>;
}